import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import { VibeAllowedRegionSchema } from "../../../shared/domain/vibe/allowed-regions";
import { VibeTenantClusterStatusSchema } from "./zadmin-vibe-tenant-cluster-list-item";

// ============================================================
// Provision
// ============================================================

export const AdminVibeTenantClusterProvisionBodySchema = z
  .object({
    organizationId: z.string().min(1),
    region: VibeAllowedRegionSchema
  })
  .strict();

/**
 * Discriminated outcome of an operator-triggered provision. Mirrors
 * `ProvisionTenantClusterOutcome` in the backend use case. Provision is
 * purely declarative — it sets desired state (a PROVISIONING row) and the
 * reconcile loop converges it — so there is no synchronous failure outcome.
 *
 *   - provisioning: a cluster row is PROVISIONING. `reprovisioned` = true
 *     when an org re-opted-in from a retired (DISABLED_RETAINED / DESTROYED)
 *     cluster, false for a first opt-in or a reused PROVISIONING row.
 *   - already_active: the org already has a live / mid-lifecycle cluster;
 *     provision is a no-op. `status` is its current state.
 */
export const AdminVibeTenantClusterProvisionOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("provisioning"),
    reprovisioned: z.boolean(),
    /**
     * Present and true only when the operator re-fired a provision against a
     * row already PROVISIONING, so this request declared nothing. Absent on a
     * fresh create and on a reprovision.
     *
     * Optional rather than required because the arm is plain `z.object`, which
     * STRIPS unknown keys: undeclared here, the backend's field never reaches
     * the operator, and a re-fire stays indistinguishable from a first provision.
     */
    reusedExistingRow: z.boolean().optional()
  }),
  z.object({ kind: z.literal("already_active"), status: VibeTenantClusterStatusSchema })
]);

/** `AdminVibeTenantClusterProvisionOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterProvisionOutcomeReadSchema = z.union([
  AdminVibeTenantClusterProvisionOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(AdminVibeTenantClusterProvisionOutcomeSchema) })
]);

// ============================================================
// Disable
// ============================================================

export const AdminVibeTenantClusterDisableBodySchema = z
  .object({
    organizationId: z.string().min(1),
    // Force the retention grace window to zero: the cluster still lands in
    // DISABLED_RETAINED, but with `retainUntil` stamped to now so the teardown
    // reaper destroys its stacks on its next tick instead of after the default
    // 30-day grace. The operator escape hatch for "stop this tenant's AWS spend
    // now" without a raw SQL `retainUntil` edit. Omitted / false = the normal
    // recoverable 30-day window. Even immediate teardown stays interruptible by
    // a re-opt-in until the reaper actually claims the cluster (CAS), so no
    // recoverability is lost beyond the grace window itself.
    immediate: z.boolean().optional()
  })
  .strict();

/**
 * Discriminated outcome of an operator-triggered disable. Mirrors
 * `DisableTenantClusterOutcome` in the backend use case.
 *
 *   - retained: the cluster is now DISABLED_RETAINED; `retainUntil` is the
 *     ISO instant the teardown reaper becomes eligible to destroy it.
 *   - already_retained: it was already retained — no-op.
 *   - not_found: the org has no dedicated cluster.
 *   - not_disablable: the cluster is in a state that is not a disable
 *     source (PROVISIONING / UPDATING / a retired state); `status` says which.
 */
export const AdminVibeTenantClusterDisableOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("retained"), retainUntil: z.string().datetime() }),
  z.object({ kind: z.literal("already_retained") }),
  z.object({ kind: z.literal("not_found") }),
  z.object({ kind: z.literal("not_disablable"), status: VibeTenantClusterStatusSchema })
]);

/** `AdminVibeTenantClusterDisableOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterDisableOutcomeReadSchema = z.union([
  AdminVibeTenantClusterDisableOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(AdminVibeTenantClusterDisableOutcomeSchema) })
]);

export type AdminVibeTenantClusterProvisionBody = z.infer<
  typeof AdminVibeTenantClusterProvisionBodySchema
>;
export type AdminVibeTenantClusterProvisionOutcome = z.infer<
  typeof AdminVibeTenantClusterProvisionOutcomeSchema
>;
export type AdminVibeTenantClusterDisableBody = z.infer<
  typeof AdminVibeTenantClusterDisableBodySchema
>;
export type AdminVibeTenantClusterDisableOutcome = z.infer<
  typeof AdminVibeTenantClusterDisableOutcomeSchema
>;
